import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

// Renders an audit entry's `changes` (an arbitrary JSON blob) readably as an expandable detail
// (US-8.6.1). Collapsed by default to keep the log scannable; expanding reveals the pretty-printed
// diff/payload. The JSON is escaped by React's text rendering — no raw HTML injection.

export interface ChangesViewerProps {
  changes: unknown;
  /** Stable id for aria-controls wiring. */
  id: string;
}

export function ChangesViewer({ changes, id }: ChangesViewerProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [open, setOpen] = useState(false);

  if (changes == null) {
    return (
      <span className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>—</span>
    );
  }

  const pretty = JSON.stringify(changes, null, 2);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={id}
        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
          isDark ? 'text-indigo-300 hover:bg-white/[0.06]' : 'text-indigo-600 hover:bg-indigo-50'
        }`}
      >
        <ChevronRight
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
        {open ? 'Hide changes' : 'View changes'}
      </button>
      {open && (
        <pre
          id={id}
          data-testid="changes-json"
          className={`mt-2 max-h-64 overflow-auto rounded-lg border p-3 font-mono text-[11px] leading-relaxed ${
            isDark
              ? 'border-white/[0.08] bg-black/30 text-white/70'
              : 'border-slate-200 bg-slate-50 text-slate-700'
          }`}
        >
          {pretty}
        </pre>
      )}
    </div>
  );
}
